import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { getAuth } from 'firebase/auth';
import { authService } from '../services/authService';
import { useNotification } from '../contexts/NotificationContext';

export const SignupPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const auth = getAuth();

  const handleEmailSignup = async (event: React.FormEvent) => {
    event.preventDefault();
    setNotice('');
    if (password.length < 8) {
      addNotification('비밀번호는 8자 이상으로 입력해 주세요.', 'error');
      return;
    }
    if (password !== confirmPassword) {
      addNotification('비밀번호 확인이 일치하지 않습니다.', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const accountEmail = await authService.signUpWithEmail(email, password);
      setNotice(`${accountEmail}로 인증 링크를 보냈습니다. 이메일 인증을 마친 뒤 로그인해 주세요.`);
      setPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      let message = '회원가입을 완료하지 못했습니다. 입력 내용을 확인하고 다시 시도해 주세요.';
      if (error.code === 'auth/email-already-in-use') message = '이미 가입된 이메일입니다. 기존 로그인 방법으로 로그인해 주세요. 다른 로그인 수단과 자동으로 계정을 합치지 않습니다.';
      else if (error.code === 'auth/invalid-email') message = '이메일 주소 형식을 확인해 주세요.';
      else if (error.code === 'auth/weak-password') message = '더 안전한 비밀번호를 입력해 주세요.';
      addNotification(message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setIsLoading(true);
    try {
      await authService.signInWithGoogle();
      addNotification('공통 계정으로 로그인했습니다. 행정 권한이 없으면 승인 대기 화면이 표시됩니다.', 'success');
      navigate('/home');
    } catch (error: any) {
      if (error.code !== 'auth/popup-closed-by-user') addNotification('Google 계정 확인을 완료하지 못했습니다. 다시 시도해 주세요.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900">
      <section className="mx-auto w-full max-w-md rounded-2xl bg-white p-7 shadow-xl dark:bg-gray-800 sm:p-9">
        <div className="mb-7 text-center">
          <img src="/Main_logo.png" alt="부다페스트 한인선교교회" className="mx-auto mb-5 h-14 w-auto" />
          <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">BFGC · 행정 포털</p>
          <h1 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">공통 계정 만들기</h1>
          <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">가입 후 이메일을 인증해 주세요. 행정 기능은 관리자가 별도로 권한을 부여한 뒤 이용할 수 있어요.</p>
        </div>

        <form onSubmit={handleEmailSignup} className="space-y-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="signup-email">이메일
            <input id="signup-email" name="email" type="email" autoComplete="email" required maxLength={254} value={email} onChange={(event) => setEmail(event.target.value)} className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" placeholder="name@example.com" />
          </label>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="signup-password">비밀번호
            <input id="signup-password" name="new-password" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" placeholder="8자 이상" />
          </label>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="signup-confirm-password">비밀번호 확인
            <input id="signup-confirm-password" name="confirm-password" type="password" autoComplete="new-password" required minLength={8} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" placeholder="비밀번호를 다시 입력해 주세요" />
          </label>
          <button type="submit" disabled={isLoading} className="w-full rounded-xl bg-indigo-600 px-4 py-3.5 font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">{isLoading ? '가입 처리 중…' : '이메일로 회원가입'}</button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-gray-400"><span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />또는<span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" /></div>
        <button type="button" onClick={() => void handleGoogleSignup()} disabled={isLoading} className="flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-4 py-3.5 font-medium text-gray-800 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-700"><FcGoogle className="h-6 w-6" />Google 계정으로 계속하기</button>
        {notice && <p role="status" className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">{notice}</p>}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-300">이미 계정이 있으신가요? <Link to="/login" className="font-semibold text-indigo-600 underline underline-offset-4 dark:text-indigo-400">로그인</Link></p>
      </section>
    </main>
  );
};
